import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { DEFAULT_FILTERS } from './index';
import { isEqual } from 'lodash';
import { parseFilters } from 'lib/helpers';
import { tv } from 'tailwind-variants';

import { selectCurrentUser } from 'features/auth/slice';
import { useSelector } from 'react-redux';

import {
  Heading,
  Icon,
  MultiCheckboxInput,
  Rule,
  useModal,
} from '@frontendmentorio/design-system-v2';

const styles = tv({
  slots: {
    button: [
      'bg-white',
      'border',
      'border-neutral-200',
      'flex',
      'gap-2',
      'items-center',
      'justify-center',
      'px-5',
      'rounded',
      'w-1/2',
      'tablet:w-max',
    ],
    container: [
      'bottom-0',
      'fixed',
      'flex',
      'items-center',
      'justify-center',
      'left-0',
      'right-0',
      'top-0',
      'z-40',
      'aria-hidden:invisible',
      '[&:not([aria-hidden="true"])>*:first-child]:bg-[hsla(0,100%,0%,0.7)]',
      '[&:not([aria-hidden="true"])_*[role="document"]]:translate-x-0',
    ],
    filtersStyles: [
      'absolute',
      'bg-white',
      'bottom-0',
      'duration-250',
      'flex',
      'flex-col',
      'h-screen',
      'max-w-[24rem]',
      'right-0',
      'top-0',
      'translate-x-full',
      'transition-transform',
      'w-full',
    ],
  },
});

const { button, container, filtersStyles } = styles();

const FILTER_KEYS = ['difficulty', 'languages', 'status', 'type'];

export default function Filter({ filters, setFilters }) {
  const currentUser = useSelector(selectCurrentUser);
  const modal = useModal();
  const [isMounted, setIsMounted] = useState(false);

  const handleChange = (data) => {
    const newFilters = parseFilters(data, filters);

    if (isEqual(newFilters, filters)) return;

    setFilters(newFilters);
  };

  const activeFilters = useMemo(() => {
    return Object.entries(filters).filter(
      ([key, value]) => FILTER_KEYS.includes(key) && value.toString()
    ).length;
  }, [filters]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <>
      <button className={button()} onClick={modal.open}>
        <Icon name="filter" /> Filter{' '}
        {activeFilters > 0 && `(${activeFilters})`}
      </button>

      {isMounted &&
        createPortal(
          <div {...modal.attr.container} className={container()}>
            <div
              {...modal.attr.overlay}
              className="absolute bg-transparent h-full w-full transition-colors"
            />

            <div className={filtersStyles()} role="document">
              <div className="border-b border-neutral-200 flex items-center justify-between p-5">
                <Heading level="4" tagName="h2">
                  Filters
                </Heading>

                <div className="flex gap-4 items-center">
                  <button
                    className="p-2 text-neutral-500 underline hover:no-underline"
                    onClick={() => setFilters(DEFAULT_FILTERS)}
                  >
                    Reset
                  </button>

                  <button aria-label="Close filters" onClick={modal.close}>
                    <Icon className="text-3xl" name="xmark" />
                  </button>
                </div>
              </div>

              <div className="flex flex-col flex-grow gap-5 min-h-0 overflow-auto p-5">
                <div
                  aria-labelledby="heading-type"
                  className="flex flex-col gap-5"
                  role="group"
                >
                  <Heading
                    className="leading-none"
                    id="heading-type"
                    level="5"
                    tagName="h3"
                  >
                    Type
                  </Heading>

                  <MultiCheckboxInput
                    className="gap-3"
                    onChange={(value) => handleChange({ type: value })}
                    options={[
                      { label: 'Free', value: 'free' },
                      { label: 'Free+', value: 'free-plus' },
                      { label: 'Premium', value: 'premium' },
                    ]}
                    value={filters.type}
                  />
                </div>

                <Rule tagName="span" />

                <div
                  aria-labelledby="heading-difficulty"
                  className="flex flex-col gap-5"
                  role="group"
                >
                  <Heading
                    className="leading-none"
                    id="heading-difficulty"
                    level="5"
                    tagName="h3"
                  >
                    Difficulty
                  </Heading>

                  <MultiCheckboxInput
                    className="gap-3"
                    onChange={(value) => handleChange({ difficulty: value })}
                    options={[
                      { label: 'Newbie', value: 1 },
                      { label: 'Junior', value: 2 },
                      { label: 'Intermediate', value: 3 },
                      { label: 'Advanced', value: 4 },
                      { label: 'Guru', value: 5 },
                    ]}
                    value={filters.difficulty}
                  />
                </div>

                <Rule tagName="span" />

                {currentUser && (
                  <div
                    aria-labelledby="heading-status"
                    className="flex flex-col gap-5"
                    role="group"
                  >
                    <Heading
                      className="leading-none"
                      id="heading-status"
                      level="5"
                      tagName="h3"
                    >
                      Status
                    </Heading>

                    <MultiCheckboxInput
                      className="gap-3"
                      onChange={(value) => handleChange({ status: value })}
                      options={[
                        { label: 'Not started', value: 'not-started' },
                        { label: 'In progress', value: 'in-progress' },
                        { label: 'Completed', value: 'completed' },
                      ]}
                      value={filters.status}
                    />

                    <Rule tagName="span" />
                  </div>
                )}

                <div
                  aria-labelledby="heading-languages"
                  className="flex flex-col gap-5"
                  role="group"
                >
                  <Heading
                    className="leading-none"
                    id="heading-languages"
                    level="5"
                    tagName="h3"
                  >
                    Languages
                  </Heading>

                  <MultiCheckboxInput
                    className="gap-3"
                    onChange={(value) => handleChange({ languages: value })}
                    options={[
                      { label: 'HTML & CSS', value: 'CSS' },
                      { label: 'JS', value: 'JS' },
                      { label: 'API', value: 'API' },
                    ]}
                    value={filters.languages}
                  />
                </div>
              </div>
            </div>
          </div>,
          document.getElementById('modal-root')
        )}
    </>
  );
}
